import { useEffect, useState, useRef } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { ClientLogosSlider } from './ui/ClientLogosSlider';
import { Quote } from 'lucide-react';
import { cn } from '@/lib/utils';
import emmaTanImage from '@/assets/emma-tan-testimonial.jpg';
import mayaPatelImage from '@/assets/maya-patel-testimonial.jpg';
import danielTanImage from '@/assets/daniel-tan-testimonial.jpg';

const BrandReportClientsSection = ({ className = '', onCtaClick }: { className?: string, onCtaClick?: () => void }) => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const testimonialRef = useRef<HTMLDivElement>(null);
  const {
    getTransform
  } = useScrollAnimation({
    intensity: 0.03
  });

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('show');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => observer.observe(el));
    return () => {
      animatedElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => {
        return (prev + 1) % testimonials.length;
      });
    }, 5000);
    return () => clearInterval(interval);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const testimonials = [{
    quote: "The brand report opened our eyes to how customers truly perceive us. It's been invaluable for our rebranding strategy.",
    author: "Emma Tan",
    position: "Head of Brand Strategy",
    avatar: "ET",
    image: emmaTanImage
  }, {
    quote: "Understanding our competitive positioning through AI analysis helped us identify gaps we never knew existed. Game-changing insights.",
    author: "Maya Patel",
    position: "Marketing VP",
    avatar: "MP",
    image: mayaPatelImage
  }, {
    quote: "The customer sentiment analysis was spot-on. We immediately implemented changes that improved our brand perception by 35%.",
    author: "Daniel Tan",
    position: "Chief Marketing Officer",
    avatar: "DT",
    image: danielTanImage
  }];

  return <section id="brand-clients-section-container" className={cn("py-24 bg-background relative overflow-hidden", className)}>
    {/* Decorative shapes with scroll-based movement */}
    <div className="absolute w-24 h-24 bg-primary/20 rounded-full top-[10%] left-[5%] shape-animation" style={{
      transform: `translate(${getTransform(0, 0.8)}px, ${getTransform(0, 0.9)}px) rotate(${getTransform(0, 0.5)}deg)`
    }}></div>
    <div className="absolute w-32 h-32 bg-accent/30 bottom-[20%] right-[5%] triangle-shape shape-animation" style={{
      animationDelay: '2s',
      transform: `translate(${getTransform(0, -0.7)}px, ${getTransform(0, -0.8)}px) rotate(${getTransform(0, -0.6)}deg)`
    }}></div>
    <div className="absolute w-16 h-16 bg-secondary/40 top-[35%] right-[15%] rounded-full shape-animation" style={{
      animationDelay: '3.5s',
      transform: `translate(${getTransform(0, -0.9)}px, ${getTransform(0, 0.6)}px) rotate(${getTransform(0, 0.8)}deg)`
    }}></div>

    <div className="container mx-auto px-6">
      <div className="text-center max-w-3xl mx-auto mb-12 animate-on-scroll">
        <h2 id="brand-clients-title" className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-foreground mb-4">Trusted by Marketing Leaders Worldwide</h2>
        <p id="brand-clients-subtitle" className="text-xl text-muted-foreground">
          See how leading brands leverage AI-powered insights to strengthen their market position.
        </p>
      </div>

      <div id="brand-clients-logos" className="animate-on-scroll">
        <ClientLogosSlider />
      </div>

      {/* Testimonial slider */}
      <div id="brand-clients-testimonials" className="mt-16 max-w-4xl mx-auto animate-on-scroll">
        <div className="relative overflow-hidden rounded-2xl bg-card/80 backdrop-blur-sm border border-border/50 p-1 shadow-elegant">
          <div ref={testimonialRef} className="flex transition-transform duration-500 ease-in-out" style={{
            transform: `translateX(-${activeTestimonial * 100 / (testimonials.length * 2)}%)`,
            width: `${testimonials.length * 200}%`
          }}>
            {/* First set of testimonials */}
            {testimonials.map((testimonial, index) => <Card key={`first-${index}`} id={`brand-testimonial-card-${index}`} className="w-full flex-shrink-0 bg-transparent border-0 shadow-none" style={{
              width: `${100 / (testimonials.length * 2)}%`
            }}>
              <CardContent className="p-8">
                <div className="grid gap-6 lg:grid-cols-[auto_1fr] items-center">
                  {/* Author Image */}
                  <div className="flex justify-center lg:justify-start order-1 lg:order-1">
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-primary rounded-xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
                      <div className="relative w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-xl overflow-hidden border-4 border-white/20 shadow-lg">
                        <img
                          src={testimonial.image}
                          alt={`${testimonial.author}, ${testimonial.position}`}
                          className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Testimonial Content */}
                  <div className="space-y-4 order-2 lg:order-2 text-center lg:text-left">
                    <div className="relative">
                      <Quote className="h-8 w-8 text-primary/30 absolute -top-1 -left-1" />
                      <blockquote id={`brand-testimonial-quote-${index}`} className="text-lg font-semibold text-foreground leading-relaxed pl-6">
                        "{testimonial.quote}"
                      </blockquote>
                    </div>
                    
                    <div className="space-y-1 pl-6">
                      <div id={`brand-testimonial-author-${index}`} className="text-lg font-bold text-foreground">
                        {testimonial.author}
                      </div>
                      <div id={`brand-testimonial-position-${index}`} className="text-sm text-primary font-medium">
                        {testimonial.position}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>)}

            {/* Duplicate testimonials for continuous scrolling */}
            {testimonials.map((testimonial, index) => <Card key={`second-${index}`} id={`brand-testimonial-card-duplicate-${index}`} className="w-full flex-shrink-0 bg-transparent border-0 shadow-none" style={{
              width: `${100 / (testimonials.length * 2)}%`
            }}>
              <CardContent className="p-8">
                <div className="grid gap-6 lg:grid-cols-[auto_1fr] items-center">
                  {/* Author Image */}
                  <div className="flex justify-center lg:justify-start order-1 lg:order-1">
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-primary rounded-xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
                      <div className="relative w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-xl overflow-hidden border-4 border-white/20 shadow-lg">
                        <img
                          src={testimonial.image}
                          alt={`${testimonial.author}, ${testimonial.position}`}
                          className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Testimonial Content */}
                  <div className="space-y-4 order-2 lg:order-2 text-center lg:text-left">
                    <div className="relative">
                      <Quote className="h-8 w-8 text-primary/30 absolute -top-1 -left-1" />
                      <blockquote id={`brand-testimonial-quote-duplicate-${index}`} className="text-lg font-semibold text-foreground leading-relaxed pl-6">
                        "{testimonial.quote}"
                      </blockquote>
                    </div>
                    
                    <div className="space-y-1 pl-6">
                      <div id={`brand-testimonial-author-duplicate-${index}`} className="text-lg font-bold text-foreground">
                        {testimonial.author}
                      </div>
                      <div id={`brand-testimonial-position-duplicate-${index}`} className="text-sm text-primary font-medium">
                        {testimonial.position}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>)}
          </div>
        </div>

        {/* Testimonial navigation dots */}
        <div id="brand-testimonial-dots" className="flex justify-center mt-6 gap-2">
          {testimonials.map((_, index) => <button key={index} id={`brand-testimonial-dot-${index}`} className={`w-3 h-3 rounded-full transition-all ${index === activeTestimonial % testimonials.length ? 'bg-primary scale-125' : 'bg-muted'}`} onClick={() => setActiveTestimonial(index)} aria-label={`View testimonial ${index + 1}`} />)}
        </div>
      </div>

      {/* CTA Button */}
      {onCtaClick && (
        <div className="text-center mt-12">
          <button 
            onClick={onCtaClick}
            className="px-8 py-4 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            See What They're Seeing
          </button>
        </div>
      )}
    </div>
  </section>;
};

export default BrandReportClientsSection;
